import React, { useEffect, useState } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Slide,
  SlideProps,
  Stack,
} from '@mui/material';
import { NotificationService } from '../../services/errorService';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
  persistent?: boolean;
}

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    // Subscribe to notification service
    const unsubscribe = NotificationService.subscribe((notification) => {
      const id = Date.now().toString();
      const newNotification: Notification = {
        id,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        duration: notification.duration || 6000,
        persistent: notification.persistent || false,
      };

      setNotifications(prev => [...prev, newNotification]);

      // Auto-remove non-persistent notifications
      if (!newNotification.persistent) {
        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== id));
        }, newNotification.duration);
      }
    });

    return unsubscribe;
  }, []);

  const handleClose = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <>
      {children}
      <Stack
        spacing={1}
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 9999,
          maxWidth: 400,
          pointerEvents: 'none', // Allow clicking through the stack container
        }}
      >
        {notifications.map((notification) => (
          <Snackbar
            key={notification.id}
            open={true}
            TransitionComponent={SlideTransition}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              position: 'relative', // Relative to the Stack
              pointerEvents: 'auto', // Re-enable pointer events for the notification itself
              mb: 1,
            }}
          >
            <Alert
              severity={notification.type}
              onClose={() => handleClose(notification.id)}
              variant="filled"
              elevation={6}
              sx={{
                width: '100%',
                boxShadow: (theme) => theme.shadows[6],
                '& .MuiAlert-message': {
                  fontSize: '0.95rem',
                },
                '& .MuiAlertTitle-root': {
                  fontWeight: 700,
                  fontSize: '1rem',
                }
              }}
            >
              {notification.title && (
                <AlertTitle>{notification.title}</AlertTitle>
              )}
              {notification.message}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </>
  );
};

export default NotificationProvider;